import React, { useState, useEffect } from 'react';
import Layout from "../../Component/Layout";
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MASTER_API, PURCHASE_API } from "../../apiConfig"; 

function ActivityRate() {
    const [rates, setRates] = useState([]);
    const [workcenters, setWorkcenters] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        workcenter_name: "",
        setup_rate: "",
        labour_rate: "",
        machine_rate: "",
        overhead_rate: "",
        currency_code: ""
    });

    useEffect(() => {
        fetchRates();
        fetchWorkcenters();
        fetchCurrencies();
    }, []);

    const fetchRates = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/activity-rate/list`);
            setRates(res.data || []);
        } catch (error) {
            console.error("Failed to fetch rates", error);
        }
    };

    const fetchWorkcenters = async () => {
        try {
            const res = await axios.get(`${MASTER_API}/workcenter`);
            setWorkcenters(res.data.data || res.data || []);
        } catch (error) {
            console.error("Failed to fetch workcenters", error);
        }
    };

    const fetchCurrencies = async () => {
        try {
            const res = await axios.get(`${MASTER_API}/configuration/currency`);
            setCurrencies(res.data || []);
        } catch (error) {
            console.error("Failed to fetch currencies", error);
        }
    };

    const handleOpenModal = (rate = null) => {
        if (rate) {
            setFormData(rate);
        } else {
            setFormData({
                id: null, workcenter_name: "", setup_rate: "", labour_rate: "",
                machine_rate: "", overhead_rate: "", currency_code: ""
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSave = async () => {
        if (!formData.workcenter_name) {
            toast.error("Workcenter is required");
            return;
        }
        if (!formData.currency_code) {
            toast.error("Currency is required");
            return;
        }
        try {
            await axios.post(`${PURCHASE_API}/activity-rate/save`, formData);
            toast.success("Activity rate saved successfully");
            fetchRates();
            handleCloseModal();
        } catch (error) {
            console.error("Save error", error);
            toast.error("Failed to save activity rate");
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${PURCHASE_API}/activity-rate/delete/${id}`);
            toast.success("Activity rate deleted");
            fetchRates();
        } catch (error) {
            console.error("Delete error", error);
            toast.error("Failed to delete activity rate");
        }
    };

    // Helper to format table cells dynamically with UOM + currency
    const formatRate = (rate, type, wcName, currencyCode) => {
        const val = parseFloat(rate);
        if (!val || val === 0) return "-";

        const wc = workcenters.find(w => w.workcenter_name === wcName);
        const uom = wc ? wc[`${type}_uom`] : "";
        return `${currencyCode ? currencyCode + ' ' : ''}${val.toFixed(2)}${uom ? ` / ${uom}` : ''}`;
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 36,
            fontSize: 13,
            borderColor: state.isFocused ? "#475569" : "#cbd5e1",
            boxShadow: "none",
            "&:hover": { borderColor: "#94a3b8" }
        }),
        option: (base, state) => ({
            ...base,
            fontSize: 13,
            backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
            color: state.isSelected ? "#fff" : "#1e293b",
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    // Find the currently selected workcenter to determine which inputs to show
    const selectedWc = workcenters.find(w => w.workcenter_name === formData.workcenter_name);

    // currency response looks like { id: 3, name: "USD" }
    const currencyOptions = currencies.map(c => ({
        value: c.name,
        label: c.name
    }));

    return (
        <Layout>
            <style>
                {`
                input[type=number]::-webkit-inner-spin-button, 
                input[type=number]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                input[type=number] {
                    -moz-appearance: textfield;
                }
                `}
            </style>

            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>

                {/* Header */}
                <div style={styles.headerCard}>
                    <div>
                        <h2 style={styles.pageTitle}>Activity Rates</h2>
                        <div style={styles.pageSubtitle}>Manage pricing for workcenter activities</div>
                    </div>
                    <button onClick={() => handleOpenModal()} style={styles.primaryBtn}>
                        + Add Rate
                    </button>
                </div>

                {/* Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Workcenter</th>
                                <th style={styles.th}>Currency</th>
                                <th style={{ ...styles.th, textAlign: "right" }}>Setup Rate</th>
                                <th style={{ ...styles.th, textAlign: "right" }}>Labour Rate</th>
                                <th style={{ ...styles.th, textAlign: "right" }}>Machine Rate</th>
                                <th style={{ ...styles.th, textAlign: "right" }}>Overhead Rate</th>
                                <th style={{ ...styles.th, textAlign: "center" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rates.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: "24px", color: "#94a3b8", fontSize: 13 }}>
                                        No activity rates configured.
                                    </td>
                                </tr>
                            ) : (
                                rates.map((r, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                        <td style={{ ...styles.td, fontWeight: 600, color: "#1e293b" }}>{r.workcenter_name}</td>
                                        <td style={{ ...styles.td, color: "#475569" }}>{r.currency_code || "-"}</td>
                                        <td style={{ ...styles.td, textAlign: "right", color: "#475569" }}>{formatRate(r.setup_rate, 'setup', r.workcenter_name, r.currency_code)}</td>
                                        <td style={{ ...styles.td, textAlign: "right", color: "#475569" }}>{formatRate(r.labour_rate, 'labour', r.workcenter_name, r.currency_code)}</td>
                                        <td style={{ ...styles.td, textAlign: "right", color: "#475569" }}>{formatRate(r.machine_rate, 'machine', r.workcenter_name, r.currency_code)}</td>
                                        <td style={{ ...styles.td, textAlign: "right", color: "#475569" }}>{formatRate(r.overhead_rate, 'overhead', r.workcenter_name, r.currency_code)}</td>
                                        <td style={{ ...styles.td, textAlign: "center" }}>
                                            <button onClick={() => handleOpenModal(r)} style={styles.actionBtn}>Edit</button>
                                            <button onClick={() => handleDelete(r.id)} style={{ ...styles.actionBtn, marginLeft: 8, color: "#dc2626" }}>Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Modal */}
                {showModal && (
                    <div style={styles.modalBackdrop}>
                        <div style={styles.modal}>
                            <div style={styles.modalHeader}>
                                <div style={{ fontWeight: 600, fontSize: 16 }}>
                                    {formData.id ? "Edit Activity Rate" : "Add Activity Rate"}
                                </div>
                                <button onClick={handleCloseModal} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer" }}>✕</button>
                            </div>
                            <div style={styles.modalBody}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={styles.label}>Workcenter</label>
                                    <Select
                                        options={workcenters.map(w => ({ value: w.workcenter_name, label: w.workcenter_name }))}
                                        value={formData.workcenter_name ? { value: formData.workcenter_name, label: formData.workcenter_name } : null}
                                        onChange={(opt) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                workcenter_name: opt ? opt.value : "",
                                                setup_rate: "", labour_rate: "", machine_rate: "", overhead_rate: ""
                                            }));
                                        }}
                                        isDisabled={!!formData.id}
                                        styles={selectStyles}
                                        placeholder="Select Workcenter..."
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <label style={styles.label}>Currency</label>
                                    <Select
                                        options={currencyOptions}
                                        value={formData.currency_code ? { value: formData.currency_code, label: formData.currency_code } : null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, currency_code: opt ? opt.value : "" }))}
                                        styles={selectStyles}
                                        placeholder="Select Currency..."
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>

                                {selectedWc && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                        {selectedWc.setup && (
                                            <div>
                                                <label style={styles.label}>Setup Rate / {selectedWc.setup_uom}</label>
                                                <input
                                                    type="number"
                                                    style={styles.input}
                                                    value={formData.setup_rate}
                                                    onChange={e => setFormData(prev => ({ ...prev, setup_rate: e.target.value }))}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        )}
                                        {selectedWc.labour && (
                                            <div>
                                                <label style={styles.label}>Labour Rate / {selectedWc.labour_uom}</label>
                                                <input
                                                    type="number"
                                                    style={styles.input}
                                                    value={formData.labour_rate}
                                                    onChange={e => setFormData(prev => ({ ...prev, labour_rate: e.target.value }))}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        )}
                                        {selectedWc.machine && (
                                            <div>
                                                <label style={styles.label}>Machine Rate / {selectedWc.machine_uom}</label>
                                                <input
                                                    type="number"
                                                    style={styles.input}
                                                    value={formData.machine_rate}
                                                    onChange={e => setFormData(prev => ({ ...prev, machine_rate: e.target.value }))}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        )}
                                        {selectedWc.overhead && (
                                            <div>
                                                <label style={styles.label}>Overhead Rate / {selectedWc.overhead_uom}</label>
                                                <input
                                                    type="number"
                                                    style={styles.input}
                                                    value={formData.overhead_rate}
                                                    onChange={e => setFormData(prev => ({ ...prev, overhead_rate: e.target.value }))}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!selectedWc && formData.workcenter_name && (
                                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 10 }}>Loading activities...</div>
                                )}
                            </div>
                            <div style={styles.modalFooter}>
                                <button onClick={handleCloseModal} style={styles.cancelBtn}>Cancel</button>
                                <button onClick={handleSave} style={styles.primaryBtn}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer position="top-right" autoClose={3000} />
            </div>
        </Layout>
    );
}

const styles = {
    headerCard: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#fff", borderRadius: 10, padding: "16px 20px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20
    },
    pageTitle: { fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 },
    pageSubtitle: { fontSize: 13, color: "#64748b", marginTop: 3 },
    primaryBtn: {
        padding: "8px 18px", background: "#1e293b", color: "#fff",
        border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600
    },
    cancelBtn: {
        padding: "8px 18px", background: "#e2e8f0", color: "#475569",
        border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600
    },
    actionBtn: {
        background: "transparent", border: "none", color: "#475569",
        cursor: "pointer", fontSize: 12, fontWeight: 600, textDecoration: "underline"
    },
    tableContainer: { background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "12px 16px", borderBottom: "2px solid #e2e8f0", color: "#475569", textAlign: "left" },
    td: { fontSize: 13, padding: "12px 16px", borderBottom: "1px solid #f1f5f9", color: "#334155" },
    modalBackdrop: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050
    },
    modal: { background: "#fff", width: 500, borderRadius: 8, overflow: "hidden", boxShadow: "0 10px 25px rgba(0,0,0,0.1)" },
    modalHeader: { display: "flex", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" },
    modalBody: { padding: "20px" },
    modalFooter: { padding: "16px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10, background: "#f8fafc" },
    label: { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 },
    input: { width: "100%", height: 36, padding: "0 10px", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", outline: "none", backgroundColor: "#fff" }
};

export default ActivityRate;